'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useWebviewBottomBarPadding } from '@/ui/layouts/bottom-bar/hooks/useWebviewBottomBarPadding'
import { gql } from '@gql-generated/gql'
import type { ProductBuyBox_ProductFragment } from '@gql-generated/graphql'
import { useAtom, useAtomValue } from 'jotai'
import { Suspense, useEffect, useMemo, useRef } from 'react'
import { useProductBuyBox } from '../../hooks/useProductBuyBox'
import { isProductPageHydratedAtom, isStickyVisibleAtom } from '../../services/product.store'
import type { MergedProduct } from '../../types/mergedProduct'
import { getProductDisplayPrice } from '../../utils/productDisplayPrice'
import { EcoTaxesDrawer } from '../eco-taxes-drawer/EcoTaxesDrawer.client'
import { ProductBuyBoxCTA } from '../product-buy-box/ProductBuyBoxCTA'
import { ProductBuyBoxMonoDiscriminantFilters } from '../product-buy-box/ProductBuyBoxMonoDiscriminantFilters.client'
import { ProductBuyBoxPrices } from '../product-buy-box/ProductBuyBoxPrices.client'
import { ProductBuyBoxPricesSkeleton } from '../product-buy-box/ProductBuyBoxPricesSkeleton'
import { ProductTiersPrices } from '../product-tiers-prices/ProductTiersPrices.client'
import { ProductBuyBoxSkeleton } from './ProductBuyBoxSkeleton.client'

gql(/* GraphQL */ `
  fragment ProductBuyBox_Cart on Cart {
    id
  }

  fragment ProductBuyBoxFields_Product on Product {
    brand {
      name
      urlKey
    }
    mainImage {
      ...FullPicture
    }
    name
    nbArticles
    reference
    type
  }

  fragment ProductBuyBox_Product on Product {
    ...ProductBuyBoxFields_Product
    brand {
      urlKey
      logo {
        ...FullPicture
      }
    }
    filters {
      ...FiltersFields
    }

    urlKey
  }

  fragment ProductBuyBoxArticle_Product on Product {
    ...ProductBuyBoxFields_Product
    description
    images {
      ...FullPicture
    }
    supplierReference
  }

  fragment ProductBuyBox_FavoritePointOfSale on PointOfSale {
    id
    phoneNumber
  }
`)

type ProductBuyBoxProps = {
  product: MergedProduct
  /** The cart id to add the product to. */
  cartId?: string

  priceAndStockIsLoading: boolean

  favoritePOSPhoneNumber?: string
}

type ProductBuyBoxStickyBarProps = {
  className: string
  children: React.ReactNode
}

/**
 * Sticky bar isolated in its own component so that `barRef` is already attached
 * when `useWebviewBottomBarPadding` runs (ProductBuyBox renders a skeleton first).
 */
function ProductBuyBoxStickyBar({ className, children }: ProductBuyBoxStickyBarProps) {
  const barRef = useRef<HTMLDivElement>(null)

  useWebviewBottomBarPadding(barRef)

  return (
    <div className={className} ref={barRef}>
      {children}
    </div>
  )
}

export function ProductBuyBox({
  product,
  cartId,
  priceAndStockIsLoading,
  favoritePOSPhoneNumber,
}: ProductBuyBoxProps) {
  const isStickyVisible = useAtomValue(isStickyVisibleAtom)
  const filterGroupRef = useRef<HTMLDivElement | null>(null)

  const displayPrice = useMemo(() => getProductDisplayPrice(product), [product])

  const {
    isProduct,
    isPublicPriceEnabled,
    availabilityIssue,
    hasPrice,
    showPricesAndEcoPart,
    isMonoDiscriminant,
  } = useProductBuyBox({ product })

  const stickyPositionClasses = useMemo(
    () =>
      twMerge(
        'desktop:bg-transparent fixed bottom-0 left-0 z-header flex w-full justify-center bg-white p-2 shadow-xl transition-transform duration-300 ease-in-out empty:hidden desktop:relative desktop:z-base desktop:w-auto desktop:justify-normal desktop:p-0 desktop:shadow-none desktop:duration-0',
        isStickyVisible || !isProduct ? 'translate-y-0' : 'desktop:translate-y-0 translate-y-full',
      ),
    [isStickyVisible, isProduct],
  )

  // Defer data-dependent rendering to after mount to avoid SSR/client mismatch
  const isMounted = useNextIsClientHydrated()

  const [isPageHydrated, setIsPageHydrated] = useAtom(isProductPageHydratedAtom)

  useEffect(() => {
    setIsPageHydrated(true)
  }, [setIsPageHydrated])

  const isHydrating = !isMounted && !isPageHydrated
  const isLoading = priceAndStockIsLoading

  if (isHydrating) {
    return <ProductBuyBoxSkeleton />
  }

  const showAvailabilityPhoneNumber =
    availabilityIssue !== null && ['storeOnly', 'noPricesAvailable'].includes(availabilityIssue)

  const restrictedProductBuyBoxMode = !hasPrice || (!showPricesAndEcoPart && !isMonoDiscriminant)

  return (
    <div
      className={twMerge(
        'relative mb-3 flex w-full flex-col gap-2 rounded-sm p-3 shadow-xl',
        restrictedProductBuyBoxMode && 'p-0 shadow-none desktop:mb-3 desktop:p-3 desktop:shadow-xl', // Prevent showing en empty shadow box When we only show a sticky link on mobile
      )}
      data-testid="product-buy-box"
    >
      {/* Price section: hidden for disconnected guests on non-public-price sites (showPricesAndEcoPart=false).
          Connected cases 1_10 (isNotSellable), 2_10 (noPricesAvailable), 3_10/3_20 (storeOnly) show the issue card here.
          Cases 4+ with price show ProductPrice. @see https://descourscabaudnet.sharepoint.com/:x:/r/sites/DigitalFactory/_layouts/15/doc.aspx?sourcedoc=%7B7b7c714a-58b2-4228-a022-953996989352%7D */}
      {showPricesAndEcoPart && (
        <Suspense fallback={<ProductBuyBoxPricesSkeleton />}>
          {isLoading ? (
            <ProductBuyBoxPricesSkeleton />
          ) : (
            <ProductBuyBoxPrices
              product={product as ProductBuyBox_ProductFragment}
              hasPrice={hasPrice}
              availabilityIssue={availabilityIssue}
              favoritePOSPhoneNumber={
                showAvailabilityPhoneNumber ? favoritePOSPhoneNumber : undefined
              }
              isPublicPriceEnabled={isPublicPriceEnabled}
            />
          )}
        </Suspense>
      )}

      {isMonoDiscriminant && (
        <ProductBuyBoxMonoDiscriminantFilters ref={filterGroupRef} filters={product.filters} />
      )}

      <ProductBuyBoxStickyBar className={stickyPositionClasses}>
        <ProductBuyBoxCTA
          isProduct={isProduct}
          isMonoDiscriminant={isMonoDiscriminant}
          hasPrice={hasPrice}
          nbArticles={product.nbArticles}
          availabilityIssue={availabilityIssue ?? undefined}
          stickyPositionClasses={stickyPositionClasses}
          product={product}
          cartId={cartId}
          filterGroupRef={filterGroupRef}
          favoritePOSPhoneNumber={favoritePOSPhoneNumber}
          isLoading={isLoading}
        />
      </ProductBuyBoxStickyBar>

      {cartId && displayPrice.tiersPrices.length > 0 && (
        <ProductTiersPrices
          tiersPrices={displayPrice.tiersPrices}
          priceQuantity={displayPrice.priceQuantity}
          packaging={product.stock?.packaging}
        />
      )}

      {showPricesAndEcoPart && !isLoading && product.ecoPart && (
        <EcoTaxesDrawer ecoTaxes={product.ecoPart} />
      )}
    </div>
  )
}
